import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { toast } from 'react-toastify';
import { getObjectPublicStatus, updateObjectBySeed, verifySeed, logObjectLocation } from '../services/api';
import './Status.css';

const Status = () => {
  const { qrCode } = useParams();
  const [objectData, setObjectData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seedInput, setSeedInput] = useState('');
  const [showSeedForm, setShowSeedForm] = useState(false);
  const [controlPanelVisible, setControlPanelVisible] = useState(false);
  const [locationRequested, setLocationRequested] = useState(false);
  
  // Ascunde footer-ul când această componentă se montează
  useEffect(() => {
    // Găsește elementul footer și ascunde-l
    const footerElement = document.querySelector('footer') || document.getElementById('footer');
    if (footerElement) {
      footerElement.style.display = 'none';
    }
    
    // Restaurează footer-ul când componenta este demontată
    return () => {
      if (footerElement) {
        footerElement.style.display = '';
      }
    };
  }, []);
  
  useEffect(() => {
    const fetchObjectData = async () => {
      setLoading(true);
      
      try {
        // Folosește endpoint-ul pentru a obține doar datele publice
        const data = await getObjectPublicStatus(qrCode);
        setObjectData(data);
        
        // Cerere pentru locație după încărcarea datelor obiectului
        if (!locationRequested) {
          requestAndSendLocation();
          setLocationRequested(true);
        }
      } catch (error) {
        toast.error("Nu am putut găsi obiectul sau a apărut o eroare.");
        console.error("Error fetching object:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchObjectData();
  }, [qrCode, locationRequested]);
  
  const requestAndSendLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          // Succes - avem locația
          const locationData = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy
          };
          
          // Trimite locația către server
          sendLocationToServer(locationData);
        },
        (error) => {
          // Utilizatorul a refuzat accesul sau altă eroare
          console.log("Eroare la obținerea locației:", error.message);
          // Nu afișăm notificare pentru a nu deranja utilizatorul
        },
        { 
          enableHighAccuracy: true,
          timeout: 5000,
          maximumAge: 0
        }
      );
    }
  };
  
  const sendLocationToServer = async (locationData) => {
    try {
      await logObjectLocation(qrCode, locationData);
      // Nu afișăm notificare pentru a nu deranja utilizatorul
      console.log("Locație trimisă cu succes");
    } catch (error) {
      console.error("Eroare la trimiterea locației:", error);
    }
  };
  
  const handleSeedSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Verifică dacă seed-ul este valid pentru acest obiect
      const result = await verifySeed(seedInput.trim());
      
      if (result.valid && result.object_id === qrCode) {
        setControlPanelVisible(true);
        toast.success('Seed corect! Panoul de control a fost deblocat.');
      } else {
        toast.error('Seed invalid. Te rugăm să încerci din nou.');
      }
    } catch (error) {
      toast.error('Seed invalid sau eroare de comunicare cu serverul.');
    }
  };
  
  const updateObjectStatus = async (newStatus) => {
    try {
      const updatedObject = await updateObjectBySeed(seedInput.trim(), {
        status: newStatus
      });
      
      // Actualizează datele locale cu informațiile de la server
      setObjectData(prevData => ({
        ...prevData,
        status: updatedObject.status
      }));
      
      toast.success(`Statusul obiectului a fost actualizat la: ${newStatus}`);
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error('Nu am putut actualiza statusul obiectului.');
    }
  };
  
  const updateVisibility = async (isVisible) => {
    try {
      const updatedObject = await updateObjectBySeed(seedInput.trim(), {
        is_public: isVisible
      });
      
      // Actualizează datele locale cu informațiile de la server
      // Trebuie să facem o nouă cerere pentru a obține formatarea corectă a datelor
      const updatedStatus = await getObjectPublicStatus(qrCode);
      setObjectData(updatedStatus);
      
      toast.success('Vizibilitatea contactelor a fost actualizată');
    } catch (error) {
      console.error("Error updating visibility:", error);
      toast.error('Nu am putut actualiza vizibilitatea contactelor.');
    }
  };
  
  if (loading) {
    return (
      <div className="status-container">
        <div className="loading">
          <div className="spinner"></div>
          <p>Se încarcă informațiile obiectului...</p>
        </div>
      </div>
    );
  }
  
  if (!objectData) {
    return (
      <div className="status-container">
        <div className="error-message">
          <h2>Obiect Negăsit</h2>
          <p>Acest cod QR nu este înregistrat în sistemul nostru. Este posibil să fi fost șters sau să nu fi existat niciodată.</p>
        </div>
      </div>
    );
  }
  
  // Get SEO title and description based on object status
  const getSeoMetadata = () => {
    if (!objectData) {
      return {
        title: 'Object Not Found | AntiFurt QR',
        description: 'This QR code is not registered in our system. It might have been deleted or never existed.'
      };
    }

    const status = objectData.status === 'active' ? 'Active' :
                  objectData.status === 'lost' ? 'Lost' :
                  objectData.status === 'stolen' ? 'Stolen' : 'Unknown';

    return {
      title: `${status} Object - ${objectData.description} | AntiFurt QR`,
      description: objectData.status === 'stolen' 
        ? `This object has been reported as stolen. Please contact the authorities if found.`
        : `View the status and details of ${objectData.description}. Object current status: ${status}.`
    };
  };

  // Funcția pentru a afișa mesajul corespunzător în funcție de starea obiectului
  const getStatusMessage = () => {
    if (objectData.status === 'active') {
      return (
        <div className="status-message active">
          <h2>🟢 Acest obiect este protejat cu Antifurt QR! 📌</h2>
          <p>💡 Dacă ai găsit acest obiect și vrei să contactezi proprietarul, verifică datele de mai jos.</p>
        </div>
      );
    } else if (objectData.status === 'lost') {
      return (
        <div className="status-message lost">
          <h2>🟠 Acest obiect a fost raportat ca PIERDUT! 🚨</h2>
          <p>💡 Dacă ai găsit acest obiect, proprietarul te roagă să-l contactezi! Orice ajutor este apreciat! 🙌</p>
        </div>
      );
    } else if (objectData.status === 'stolen') {
      return (
        <div className="status-message stolen">
          <h2>🔴 ALERTĂ! Acest obiect a fost raportat ca FURAT! 🚨</h2>
          <p>👀 Dacă vezi acest obiect în posesia altcuiva, te rugăm să anunți autoritățile locale. Proprietarul caută informații pentru recuperare.</p>
        </div>
      );
    }
    return null;
  };

  // Funcția pentru a afișa butonul de contact
  const getContactButton = () => {
    if (objectData.contact) {
      return (
        <div className="contact-button-container">
          <a 
            href={`tel:${objectData.contact.phone_number}`} 
            className="contact-button"
          >
            📞 Contactează Proprietarul
          </a>
        </div>
      );
    }
    return null;
  };

  return (
    <>
      <Helmet>
        <title>{getSeoMetadata().title}</title>
        <meta name="description" content={getSeoMetadata().description} />
        <style type="text/css">{`
          footer, .footer, #footer {
            display: none !important;
          }
        `}</style>
      </Helmet>
      <div className="status-container">
        <div className={`status-card ${objectData.status === 'stolen' ? 'stolen' : objectData.status === 'lost' ? 'lost' : 'active'}`}>
          {/* Mesajul de status corespunzător */}
          {getStatusMessage()}
          
          <div className="object-info">
            <div className="info-group">
              <h3>Descriere</h3>
              <p>{objectData.description}</p>
            </div>
            
            {objectData.contact && (
              <div className="contact-info">
                <h3>Contact Proprietar</h3>
                <p><strong>Nume:</strong> {objectData.contact.full_name}</p>
                <p><strong>Telefon:</strong> {objectData.contact.phone_number}</p>
              </div>
            )}
            
            {!objectData.contact && objectData.status !== 'stolen' && (
              <div className="private-message">
                <p>Proprietarul a ales să păstreze informațiile de contact private.</p>
              </div>
            )}
            
            <div className="status-info">
              <h3>Status</h3>
              <div className={`status-badge ${objectData.status}`}>
                {objectData.status === 'active' ? 'Activ' : 
                 objectData.status === 'lost' ? 'Pierdut' : 
                 objectData.status === 'stolen' ? 'Furat' : 'Necunoscut'}
              </div>
            </div>
          </div>
          
          {/* Buton de contact pentru proprietar */}
          {getContactButton()}
          
          {/* Hidden admin link - poate fi accesat prin adăugare de /admin la URL */}
          <div className="hidden-admin-link" style={{ fontSize: '0.1px', color: 'transparent', position: 'absolute', bottom: 0, right: 0 }}>
            <a href={`/qr/${qrCode}/admin`} style={{ color: 'transparent' }}>admin</a>
          </div>
          
          {showSeedForm && !controlPanelVisible && (
            <div className="seed-form">
              <h3>Introdu seed-ul de 12 cuvinte pentru a accesa panoul de control</h3>
              <form onSubmit={handleSeedSubmit}>
                <textarea
                  value={seedInput}
                  onChange={(e) => setSeedInput(e.target.value)}
                  placeholder="Introdu seed-ul de 12 cuvinte aici"
                  required
                />
                <div className="form-buttons">
                  <button type="submit" className="btn primary-btn">Trimite</button>
                  <button 
                    type="button" 
                    className="btn secondary-btn"
                    onClick={() => setShowSeedForm(false)}
                  >
                    Anulează
                  </button>
                </div>
              </form>
            </div>
          )}
          
          {controlPanelVisible && (
            <div className="control-panel">
              <h2>Panoul de Control al Proprietarului</h2>
              
              <div className="control-section">
                <h3>Actualizează Status Obiect</h3>
                <div className="button-group">
                  <button 
                    className={`status-btn active ${objectData.status === 'active' ? 'selected' : ''}`}
                    onClick={() => updateObjectStatus('active')}
                  >
                    Marchează ca Activ
                  </button>
                  <button 
                    className={`status-btn lost ${objectData.status === 'lost' ? 'selected' : ''}`}
                    onClick={() => updateObjectStatus('lost')}
                  >
                    Raportează ca Pierdut
                  </button>
                  <button 
                    className={`status-btn stolen ${objectData.status === 'stolen' ? 'selected' : ''}`}
                    onClick={() => updateObjectStatus('stolen')}
                  >
                    Raportează ca Furat
                  </button>
                </div>
              </div>
              
              <div className="control-section">
                <h3>Vizibilitate Informații Contact</h3>
                <div className="button-group">
                  <button 
                    className={`visibility-btn ${objectData.contact ? 'selected' : ''}`}
                    onClick={() => updateVisibility(true)}
                  >
                    Fă Informațiile Publice
                  </button>
                  <button 
                    className={`visibility-btn ${!objectData.contact && objectData.status !== 'stolen' ? 'selected' : ''}`}
                    onClick={() => updateVisibility(false)}
                  >
                    Fă Informațiile Private
                  </button>
                </div>
                <p className="visibility-info">
                  {objectData.contact
                    ? 'Informațiile tale de contact sunt vizibile pentru oricine scanează codul QR.'
                    : objectData.status === 'stolen'
                      ? 'Informațiile tale de contact sunt vizibile deoarece obiectul este marcat ca furat.'
                      : 'Informațiile tale de contact sunt ascunse vederii publice.'}
                </p>
              </div>
              
              <button 
                className="exit-btn"
                onClick={() => {
                  setControlPanelVisible(false);
                  setShowSeedForm(false);
                  setSeedInput('');
                }}
              >
                Ieșire din Panoul de Control
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Status;